import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectModal,
  setModal,
  selectCurrentProject,
} from '../../redux/application-slice'
import { ProjectDocumentMetadata } from '../../shared/interfaces/project/document/document.interface'
import DocumentListboxMulti from '../document-listbox/document-listbox-multi'
import { useCreateRevisionExportWordMutation } from '../../redux/api/api-revisions-slice'
import { toast } from 'react-toastify'

interface ExportWordDocsModalProps {
  open: boolean
}

const ExportWordDocsModal: React.FC<ExportWordDocsModalProps> = ({ open }) => {
  const [isOpen, setIsOpen] = useState<boolean>(open)
  const dispatch = useDispatch()
  const modal = useSelector(selectModal)
  const currentProject = useSelector(selectCurrentProject)
  const documents = modal?.documents as ProjectDocumentMetadata[] | undefined
  const [selectedDocuments, setSelectedDocuments] = useState<
    ProjectDocumentMetadata[]
  >([])

  const [createRevisionExportWord, { isLoading: isExporting }] =
    useCreateRevisionExportWordMutation()

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const onClose = useCallback(() => {
    dispatch(setModal(null))
    setIsOpen(false)
    setSelectedDocuments([])
  }, [dispatch])

  const onExport = useCallback(async () => {
    const selectedDocumentIds = selectedDocuments.map((doc) =>
      doc.id.toString()
    )

    try {
      await createRevisionExportWord({
        documentIds: selectedDocumentIds,
      }).unwrap()

      toast.success('Exported word files will be emailed to you shortly.')
      onClose()
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
    }
  }, [selectedDocuments, createRevisionExportWord, onClose])

  if (!documents) {
    return null
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} as="div" className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mt-3 text-left sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Export to Word
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500">
                        Select documents to include in the Word export.
                      </p>
                    </div>
                    <div className="mt-4">
                      <DocumentListboxMulti
                        documents={documents}
                        selectedDocuments={selectedDocuments}
                        setSelectedDocuments={setSelectedDocuments}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                      isExporting ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                    onClick={onExport}
                    disabled={isExporting || selectedDocuments.length === 0}
                  >
                    {isExporting ? 'Exporting...' : 'Export'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ExportWordDocsModal
